import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loader from "../components/loader";
import axiosInstance from "../services/baseurl";
import {
  activeBatchEndPoint,
  dashboadDetailsEndPoint,
  recDrawnBatchEndPoint,
  userByIdEndpoint,
  batchByTransactions,
} from "../services/endpoints";
import {
  CalendarIcon,
  TokenRupeeIcon,
  TrophyIcon,
  MoneyBillIcon,
  TikcetIcon,
} from "../assets/icon";
import moment from "moment";
import "../css/dashboard.css";
import "../css/dashboardGlass.css";
import styles from "../css/createBatchDesign.module.css";
import dashboardStyles from "../css/dashboardDesign.module.css";

const money = (value) =>
  value == null
    ? "Not available"
    : new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(Number(value) || 0);
const count = (value) =>
  value == null
    ? "Not available"
    : new Intl.NumberFormat("en-IN").format(Number(value) || 0);
const date = (value) =>
  value && moment(value).isValid()
    ? moment(value).format("DD MMM YYYY")
    : "Not scheduled";
const videoLink = (value) => {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

function Metric({ label, value, icon, note }) {
  return (
    <article className={dashboardStyles.metric}>
      <span className={dashboardStyles.metricIcon}>{icon}</span>
      <span className={dashboardStyles.metricLabel}>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

export default function Dashboard() {
  const [activeBatch, setActiveBatch] = useState(null);
  const [today, setToday] = useState(null);
  const [recentBatch, setRecentBatch] = useState(null);
  const [winner, setWinner] = useState(null);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    const recordError = (key, message) => {
      if (!cancelled)
        setErrors((previous) => ({ ...previous, [key]: message }));
    };
    const request = async (endpoint, key, config) => {
      try {
        const response = await axiosInstance.get(endpoint, config);
        if (response.data.status !== "Success")
          throw new Error(response.data.message || "Unable to load details.");
        return response.data.payload;
      } catch (error) {
        recordError(
          key,
          error.response?.data?.message ||
            error.message ||
            "Unable to load details.",
        );
        return null;
      }
    };
    const loadActive = async () => {
      const batch = await request(activeBatchEndPoint, "active");
      if (cancelled) return;
      setActiveBatch(batch || null);
      if (batch?.batchId) {
        const transactions = await request(
          `${batchByTransactions}?batchId=${batch.batchId}`,
          "totals",
        );
        if (cancelled || !Array.isArray(transactions)) return;
        const completed = transactions.filter(
          (item) => item.state === "COMPLETED",
        );
        setTotals({
          totalAmount: completed.reduce(
            (sum, item) => sum + (Number(item.itemAmount) || 0),
            0,
          ),
          noOfTransections: new Set(
            completed.map((item) => item.ppTransectionId || item.transectionID),
          ).size,
          noOfOrders: new Set(completed.map((item) => item.orderId)).size,
        });
      }
    };
    const loadToday = async () => {
      const details = await request(dashboadDetailsEndPoint, "today", {
        timeout: 120000,
      });
      if (!cancelled) setToday(details?.today || null);
    };
    const loadRecent = async () => {
      const batch = await request(recDrawnBatchEndPoint, "recent");
      if (cancelled) return;
      setRecentBatch(batch || null);
      if (batch?.winnerId) {
        const details = await request(
          userByIdEndpoint + batch.winnerId,
          "winner",
        );
        if (!cancelled) setWinner(details || null);
      }
    };
    Promise.allSettled([loadActive(), loadToday(), loadRecent()]).then(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const images = Array.isArray(activeBatch?.images)
    ? activeBatch.images.filter((image) => image?.imageURL)
    : [];
  const preview = activeBatch?.phoneId || images[0]?.imageURL;
  const video = videoLink(recentBatch?.winnerURL || recentBatch?.url);
  const recentImages = [
    recentBatch?.phoneURL || recentBatch?.phoneId,
    recentBatch?.imageURL,
  ].filter(Boolean);

  return (
    <div className="dashboard-glass admin-theme">
      {loading && <Loader />}
      <div className={dashboardStyles.fullWidth}>
          <main className={styles.workspace}>
            <header className={styles.pageHeader}>
              <div>
                <span className={styles.eyebrow}>YOUR WORKSPACE</span>
                <h1>Dashboard</h1>
                <p>A clear view of your sales, collections, and latest draw.</p>
              </div>
              <Link
                to="/crateBatch"
                className={`${styles.primaryButton} ${dashboardStyles.createLink}`}
              >
                Create a batch <span aria-hidden="true">&#8599;</span>
              </Link>
            </header>
            <section
              aria-labelledby="today-overview"
              className={dashboardStyles.overview}
            >
              <div className={dashboardStyles.sectionHeading}>
                <div>
                  <h2 id="today-overview">Today's overview</h2>
                  <p>Your daily activity at a glance.</p>
                </div>
                <span className={styles.draftBadge}>
                  <span />
                  Today
                </span>
              </div>
              {errors.today && (
                <p role="status" className={dashboardStyles.error}>
                  Today's figures could not be loaded. Please refresh to try
                  again.
                </p>
              )}
              <div className={dashboardStyles.metrics}>
                <Metric
                  label="Sales amount"
                  value={money(today?.totalAmount)}
                  icon={<TokenRupeeIcon />}
                  note="Today's sales"
                />
                <Metric
                  label="Transactions"
                  value={count(today?.noOfTransections)}
                  icon={<MoneyBillIcon />}
                  note="Today's payments"
                />
                <Metric
                  label="Tokens sold"
                  value={count(today?.noOfOrders)}
                  icon={<TikcetIcon />}
                  note="Today's token sales"
                />
              </div>
            </section>
            <section
              aria-labelledby="batch-performance"
              className={dashboardStyles.overview}
            >
              <div className={dashboardStyles.sectionHeading}>
                <div>
                  <h2 id="batch-performance">Active batch performance</h2>
                  <p>Completed sales for your current batch.</p>
                </div>
                {activeBatch?.batchId && (
                  <Link
                    to="/transactions"
                    state={{ id: activeBatch.batchId }}
                    className={dashboardStyles.textLink}
                  >
                    View transactions <span aria-hidden="true">&#8599;</span>
                  </Link>
                )}
              </div>
              {errors.totals && (
                <p role="status" className={dashboardStyles.error}>
                  Batch totals could not be loaded. Please refresh to try again.
                </p>
              )}
              <div className={dashboardStyles.metrics}>
                <Metric
                  label="Total sales"
                  value={money(totals?.totalAmount)}
                  icon={<TokenRupeeIcon />}
                  note="Completed sales in this batch"
                />
                <Metric
                  label="Transactions"
                  value={count(totals?.noOfTransections)}
                  icon={<MoneyBillIcon />}
                  note="Unique completed payments"
                />
                <Metric
                  label="Tokens sold"
                  value={count(totals?.noOfOrders)}
                  icon={<TikcetIcon />}
                  note="Orders in this batch"
                />
              </div>
            </section>
            <div className={dashboardStyles.detailLayout}>
              <section
                className={styles.panel}
                aria-labelledby="active-draw-title"
              >
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>
                    <span className={styles.step}>01</span>
                    <div>
                      <h2 id="active-draw-title">Active draw</h2>
                      <p>Your current collection and draw details.</p>
                    </div>
                  </div>
                  {activeBatch?.batchId && (
                    <span className={dashboardStyles.liveBadge}>
                      <span /> Active
                    </span>
                  )}
                </div>
                {activeBatch?.batchId ? (
                  <>
                    <div className={dashboardStyles.activePreview}>
                      {preview ? (
                        <img
                          src={preview}
                          alt={
                            activeBatch.phoneName ||
                            images[0]?.imageName ||
                            "Active draw preview"
                          }
                        />
                      ) : (
                        <div className={dashboardStyles.noImage}>
                          Collection preview unavailable
                        </div>
                      )}
                      <span className={dashboardStyles.previewLabel}>
                        BATCH #{activeBatch.batchId}
                      </span>
                    </div>
                    <dl className={dashboardStyles.drawFacts}>
                      <div>
                        <dt>
                          <TokenRupeeIcon /> Token price
                        </dt>
                        <dd>{money(activeBatch.amount)}</dd>
                      </div>
                      <div>
                        <dt>
                          <CalendarIcon /> Draw date
                        </dt>
                        <dd>{date(activeBatch.drawDate)}</dd>
                      </div>
                      <div>
                        <dt>Collection</dt>
                        <dd>
                          {images.length}{" "}
                          {images.length === 1 ? "image" : "images"}
                        </dd>
                      </div>
                    </dl>
                    {images.length > 0 && (
                      <div className={dashboardStyles.thumbnails}>
                        {images.map((image, index) => (
                          <img
                            key={`${image.imageId}-${index}`}
                            src={image.imageURL}
                            alt={
                              image.imageName || `Collection item ${index + 1}`
                            }
                            loading="lazy"
                          />
                        ))}
                      </div>
                    )}
                    <Link to="/uploads" className={dashboardStyles.textLink}>
                      Explore all batches{" "}
                      <span aria-hidden="true">&#8599;</span>
                    </Link>
                  </>
                ) : (
                  <div className={dashboardStyles.empty}>
                    <span className={styles.uploadIcon}>
                      <CalendarIcon />
                    </span>
                    <h3>
                      {errors.active
                        ? "Draw details unavailable"
                        : "No active draw yet"}
                    </h3>
                    <p>
                      {errors.active
                        ? "Refresh to try loading the active draw again."
                        : "Your next active batch will appear here with its collection and draw date."}
                    </p>
                    <Link to="/uploads" className={dashboardStyles.textLink}>
                      Browse batches <span aria-hidden="true">&#8599;</span>
                    </Link>
                  </div>
                )}
              </section>
              <div className={dashboardStyles.rightColumn}>
                <section
                  className={styles.panel}
                  aria-labelledby="last-winner-title"
                >
                  <div className={styles.sectionTitle}>
                    <span className={styles.step}>02</span>
                    <div>
                      <h2 id="last-winner-title">Latest draw winner</h2>
                      <p>A look at the most recent result.</p>
                    </div>
                  </div>
                  {recentBatch?.batchId ? (
                    <>
                      <div className={dashboardStyles.winnerSpotlight}>
                        <span className={dashboardStyles.winnerIcon}>
                          <TrophyIcon />
                        </span>
                        <div>
                          <small>WINNER</small>
                          <h3>
                            {winner?.name ||
                              (errors.winner
                                ? "Details unavailable"
                                : "Not announced")}
                          </h3>
                          <p>
                            {winner?.phone ||
                              "Winner details will appear here."}
                          </p>
                        </div>
                      </div>
                      <dl className={dashboardStyles.winnerFacts}>
                        <div>
                          <dt>Batch</dt>
                          <dd>#{recentBatch.batchId}</dd>
                        </div>
                        <div>
                          <dt>Draw date</dt>
                          <dd>{date(recentBatch.drawDate)}</dd>
                        </div>
                        <div>
                          <dt>Token price</dt>
                          <dd>{money(recentBatch.amount)}</dd>
                        </div>
                      </dl>
                      {recentImages.length > 0 && (
                        <div className={dashboardStyles.recentImages}>
                          {recentImages.map((url, index) => (
                            <img
                              key={`${url}-${index}`}
                              src={url}
                              alt={`Latest draw preview ${index + 1}`}
                              loading="lazy"
                            />
                          ))}
                        </div>
                      )}
                      {video && (
                        <a
                          href={video}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={dashboardStyles.textLink}
                        >
                          Watch winner video{" "}
                          <span aria-hidden="true">&#8599;</span>
                        </a>
                      )}
                    </>
                  ) : (
                    <div className={dashboardStyles.empty}>
                      <span className={styles.uploadIcon}>
                        <TrophyIcon />
                      </span>
                      <h3>
                        {errors.recent
                          ? "Latest result unavailable"
                          : "A winner is on the way"}
                      </h3>
                      <p>
                        {errors.recent
                          ? "Refresh to try loading the latest result again."
                          : "Once a draw is completed, the winner and result will appear here."}
                      </p>
                    </div>
                  )}
                </section>
                <section
                  className={`${styles.panel} ${dashboardStyles.quickActions}`}
                  aria-labelledby="quick-actions-title"
                >
                  <span className={styles.eyebrow}>KEEP THINGS MOVING</span>
                  <h2 id="quick-actions-title">Quick actions</h2>
                  {[
                    [
                      "/crateBatch",
                      "Create a batch",
                      "Start a new image collection",
                    ],
                    [
                      "/updateBatch",
                      "Update a batch",
                      "Manage your existing collections",
                    ],
                    ["/userlist", "View users", "Browse your customer list"],
                  ].map(([path, title, description]) => (
                    <Link to={path} key={path}>
                      <div>
                        <strong>{title}</strong>
                        <small>{description}</small>
                      </div>
                      <span aria-hidden="true">&#8599;</span>
                    </Link>
                  ))}
                </section>
              </div>
            </div>
          </main>
      </div>
    </div>
  );
}
